import React, { useCallback, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { apiKeysAPI } from '../api/api-keys'
import type { ApiKey } from '../api/api-keys'
import { Button, Card, Alert, Input } from './ui'
import { Modal } from './ui/Modal'

interface ApiKeyTableProps {
  orgId: string
}

export const ApiKeyTable: React.FC<ApiKeyTableProps> = ({ orgId }) => {
  const intl = useIntl()
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Create form state
  const [showCreate, setShowCreate] = useState(false)
  const [keyName, setKeyName] = useState('')
  const [creating, setCreating] = useState(false)

  // Created key reveal modal
  const [revealedKey, setRevealedKey] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const loadKeys = useCallback(async () => {
    try {
      setLoading(true)
      const data = await apiKeysAPI.list(orgId)
      setKeys(data)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : intl.formatMessage({ id: 'apikey.error_load' }))
    } finally {
      setLoading(false)
    }
  }, [orgId])

  useEffect(() => { loadKeys() }, [loadKeys])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!keyName.trim()) return
    try {
      setCreating(true)
      setError(null)
      const created = await apiKeysAPI.create(orgId, keyName.trim())
      setRevealedKey(created.full_key)
      setKeyName('')
      setShowCreate(false)
      loadKeys()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : intl.formatMessage({ id: 'apikey.error_create' }))
    } finally {
      setCreating(false)
    }
  }

  const handleRevoke = async (keyId: string) => {
    if (!confirm(intl.formatMessage({ id: 'apikey.revoke_confirm' }))) return
    try {
      await apiKeysAPI.revoke(orgId, keyId)
      loadKeys()
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : intl.formatMessage({ id: 'apikey.error_revoke' }))
    }
  }

  const handleCopy = async () => {
    if (!revealedKey) return
    await navigator.clipboard.writeText(revealedKey)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const activeKeys = keys.filter(k => !k.is_revoked)
  const revokedKeys = keys.filter(k => k.is_revoked)

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-foreground-muted">
          <FormattedMessage id="apikey.description" />
        </p>
        <Button size="sm" onClick={() => setShowCreate(true)}><FormattedMessage id="apikey.create_button" /></Button>
      </div>

      {error && <Alert variant="error" className="mb-4">{error}</Alert>}

      {/* Create form */}
      {showCreate && (
        <Card className="mb-4" padding="sm">
          <form onSubmit={handleCreate} className="flex gap-3 items-end p-4">
            <div className="flex-1">
              <Input
                label={intl.formatMessage({ id: 'apikey.key_name_label' })}
                value={keyName}
                onChange={e => setKeyName(e.target.value)}
                placeholder={intl.formatMessage({ id: 'apikey.key_name_placeholder' })}
                required
              />
            </div>
            <Button type="submit" loading={creating}>
              {creating ? <FormattedMessage id="apikey.creating" /> : <FormattedMessage id="apikey.create" />}
            </Button>
            <Button variant="ghost" onClick={() => setShowCreate(false)}><FormattedMessage id="apikey.cancel" /></Button>
          </form>
        </Card>
      )}

      {/* Key reveal modal */}
      <Modal
        open={!!revealedKey}
        onClose={() => { setRevealedKey(null); setCopied(false) }}
        title={intl.formatMessage({ id: 'apikey.modal_title' })}
      >
        <Alert variant="warning" className="mb-4">
          <FormattedMessage id="apikey.copy_warning" />
        </Alert>
        <div className="bg-background-alt rounded-lg p-3 font-mono text-sm break-all mb-4">
          {revealedKey}
        </div>
        <div className="flex gap-2">
          <Button onClick={handleCopy} variant={copied ? 'secondary' : 'primary'}>
            {copied ? <FormattedMessage id="apikey.copied" /> : <FormattedMessage id="apikey.copy_clipboard" />}
          </Button>
          <Button variant="ghost" onClick={() => { setRevealedKey(null); setCopied(false) }}>
            <FormattedMessage id="apikey.done" />
          </Button>
        </div>
      </Modal>

      {/* Active keys table */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
        </div>
      ) : activeKeys.length === 0 && revokedKeys.length === 0 ? (
        <div className="text-center py-8 text-foreground-muted">
          <FormattedMessage id="apikey.empty" />
        </div>
      ) : (
        <>
          {activeKeys.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="apikey.col_name" /></th>
                    <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="apikey.col_key" /></th>
                    <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="apikey.col_created" /></th>
                    <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="apikey.col_last_used" /></th>
                    <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="apikey.col_requests" /></th>
                    <th className="pb-3 font-semibold text-foreground text-right"><FormattedMessage id="apikey.col_actions" /></th>
                  </tr>
                </thead>
                <tbody>
                  {activeKeys.map(k => (
                    <tr key={k.id} className="border-b border-border last:border-0">
                      <td className="py-3 font-medium text-foreground">{k.name}</td>
                      <td className="py-3 font-mono text-foreground-muted text-xs">{k.key_hint}</td>
                      <td className="py-3 text-foreground-muted">{new Date(k.created_at).toLocaleDateString()}</td>
                      <td className="py-3 text-foreground-muted">
                        {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : intl.formatMessage({ id: 'apikey.never' })}
                      </td>
                      <td className="py-3 text-foreground-muted">{k.request_count.toLocaleString()}</td>
                      <td className="py-3 text-right">
                        <Button variant="danger" size="sm" onClick={() => handleRevoke(k.id)}>
                          <FormattedMessage id="apikey.revoke" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {revokedKeys.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-foreground-muted mb-2"><FormattedMessage id="apikey.revoked_keys" /></h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm opacity-60">
                  <tbody>
                    {revokedKeys.map(k => (
                      <tr key={k.id} className="border-b border-border last:border-0">
                        <td className="py-2 text-foreground-muted line-through">{k.name}</td>
                        <td className="py-2 font-mono text-foreground-muted text-xs">{k.key_hint}</td>
                        <td className="py-2 text-foreground-muted">{new Date(k.created_at).toLocaleDateString()}</td>
                        <td className="py-2 text-error text-xs"><FormattedMessage id="apikey.revoked" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
